import { useState, useEffect, useCallback } from "react";
import { AlertTriangleIcon, WindIcon, ZapIcon, HomeIcon, CloudRainIcon, MapPinIcon, RadioIcon, RefreshCwIcon, PlayCircleIcon, ExternalLinkIcon, ShieldAlertIcon, XCircleIcon, CheckCircle2Icon, LoaderIcon, FacebookIcon, NewspaperIcon, ThumbsUpIcon, MessageCircleIcon, Share2Icon, PhoneIcon, NavigationIcon, SatelliteIcon, TvIcon, HeartIcon } from "lucide-react";
interface NWSAlert {
  id: string;
  event: string;
  headline: string;
  severity: "Extreme" | "Severe" | "Moderate" | "Minor";
  effective: string;
  expires: string;
  description: string;
  areaDesc: string;
}
interface NWSForecast {
  name: string;
  temperature: number;
  windSpeed: string;
  windDirection: string;
  shortForecast: string;
}
interface FBPost {
  id: string;
  message: string;
  created_time: string;
  from?: {
    name: string;
  };
  likes?: number;
  comments?: number;
  shares?: number;
}
type FBStatus = "disconnected" | "connecting" | "connected" | "error";
type NWSStatus = "idle" | "loading" | "ok" | "error";

// ── Verified ticker data (sources: MEMA mauicounty.gov, NWS HFO, Hawaiian Electric) ──
const TICKER_ITEMS = ["🔴 FLASH FLOOD WARNING — Maui until 8 PM HST · Source: NWS HFO", "🌀 HIGH WIND WARNING — Haleakalā gusts to 85 mph · Source: NWS HFO", "🚧 HANA HWY INACCESSIBLE — multiple blockages · Source: MEMA mauicounty.gov", "⚡ ~20,000 without power at peak on Maui · now ~10K restored · Source: Hawaiian Electric", "🏠 6 shelters open — 100+ sheltering across Maui County · Source: MEMA / Red Cross", "🚗 South & North Kihei Rd CLOSED — Iao Valley Rd CLOSED · Source: MEMA", "🌧️ 20+ inches rain recorded — Kula / south Haleakalā slopes · Source: NWS HFO", "📋 Submit storm damage: crisistrack.juvare.com/public/mauiHI", "🔁 Storm weakening forecast Sunday–Monday — NWS HFO"];

// ── Verified breaking news (all URLs confirmed real via web search) ──
const BREAKING_NEWS = [{
  id: 1,
  severity: "red",
  time: "Mar 14, 3:42 PM",
  source: "Maui Now",
  text: "Flash Flood Warning extended for Maui until 8 PM HST",
  url: "https://mauinow.com/2026/03/14/powerful-kona-storm-brings-warning-level-winds-and-flash-flood-risks-flood-warning-issued-for-maui-until-2-p-m/"
}, {
  id: 2,
  severity: "red",
  time: "Mar 14, 2:15 PM",
  source: "MEMA",
  text: "Hana Highway inaccessible — multiple blockages reported",
  url: "https://www.mauicounty.gov/m/newsflash/home/detail/18403"
}, {
  id: 3,
  severity: "red",
  time: "Mar 14, 1:30 PM",
  source: "Hawaii News Now",
  text: "Dozens cut off in Hāna due to road washout — sheltering at Hāna High",
  url: "https://www.hawaiinewsnow.com/2026/03/14/widespread-flooding-damage-underway-storm-batters-maui-county/"
}, {
  id: 4,
  severity: "red",
  time: "Mar 14, 12:00 PM",
  source: "Hawaii News Now",
  text: "Iao Valley home swept partially into stream by floodwaters — 'it just went boom'",
  url: "https://www.hawaiinewsnow.com/video/2026/03/15/it-just-went-boom-maui-couple-watches-stream-erode-home-during-storm/"
}, {
  id: 5,
  severity: "orange",
  time: "Mar 14, 11:00 AM",
  source: "Hawaii News Now",
  text: "Maui Fire Dept conducts overnight floodwater rescues in Kīhei",
  url: "https://www.hawaiinewsnow.com/2026/03/14/residents-seek-shelter-maui-hit-with-rising-winds-flooding/"
}, {
  id: 6,
  severity: "orange",
  time: "Mar 14, 9:45 AM",
  source: "Maui County",
  text: "South & North Kihei Rd closed; Iao Valley Rd closed — road washed away",
  url: "https://www.mauicounty.gov/m/newsflash/home/detail/18403"
}, {
  id: 7,
  severity: "orange",
  time: "Mar 14, 8:00 AM",
  source: "KITV4",
  text: "~20,000 Hawaiian Electric customers without power on Maui at peak; 67K+ statewide",
  url: "https://www.kitv.com/news/local/over-139-000-hawaiian-electric-customers-left-in-the-dark-amid-kona-low-storm/article_a41ed173-a3c8-4305-9bd2-25ce2edd3799.html"
}, {
  id: 8,
  severity: "orange",
  time: "Mar 14, 7:30 AM",
  source: "Maui Now",
  text: "Iao Valley flooding — portions of road washed away, driveway access cut off",
  url: "https://mauinow.com/2026/03/14/county-of-maui-kona-storm-recap-saturday-march-14/"
}, {
  id: 9,
  severity: "orange",
  time: "Mar 14, 7:00 AM",
  source: "KHON2",
  text: "Kamehameha V Hwy near Kawela Gulch inaccessible — landslide reported",
  url: "https://www.khon2.com/maui-news/kona-storm-brings-heavy-rain-and-flooding-to-maui-county/"
}, {
  id: 10,
  severity: "yellow",
  time: "Mar 14, 4:00 AM",
  source: "NWS Honolulu",
  text: "Storm forecast to weaken and lift NE Sunday–Monday — trade winds return by Thursday",
  url: "https://www.weather.gov/hfo"
}, {
  id: 11,
  severity: "yellow",
  time: "Mar 13, 6:00 AM",
  source: "Maui Now",
  text: "All Maui County schools, state offices, UH campuses closed statewide",
  url: "https://mauinow.com/2026/03/13/county-of-maui-kona-storm-updates/"
}, {
  id: 12,
  severity: "yellow",
  time: "Mar 13, 5:00 AM",
  source: "Star-Advertiser",
  text: "Oahu, Kauai under flood advisories — 115K+ customers without power statewide",
  url: "https://www.staradvertiser.com/2026/03/13/breaking-news/oahu-kauai-under-flood-advisories-as-kona-low-storm-pounds-state/"
}, {
  id: 13,
  severity: "yellow",
  time: "Mar 13, 3:00 AM",
  source: "The Watchers",
  text: "130,000+ customers lose power across Hawaii as Kona low brings damaging winds & flooding",
  url: "https://watchers.news/2026/03/14/over-130-000-customers-lose-power-across-hawaii-as-kona-low-brings-damaging-winds-and-flash-flooding/"
}, {
  id: 14,
  severity: "yellow",
  time: "Mar 10, 5:00 PM",
  source: "Maui County",
  text: "Mayor Bissen signs emergency proclamation — state/federal aid unlocked",
  url: "https://www.mauicounty.gov/CivicAlerts.aspx?AID=18315"
}];

// ── MEMA alerts (all URLs verified from mauicounty.gov) ──
const MEMA_ALERTS = [{
  id: "m1",
  date: "Mar 14, 2026",
  level: "red",
  badge: "ACTIVE ALERT",
  title: "Noon Update: Hana Hwy Inaccessible, 10K Without Power",
  summary: "Hana Highway inaccessible — multiple road blockages. South & North Kihei Rd closed. ~10,000 without power on Maui as of noon. Maui Bus service suspended. Residents urged to stay off roads. Curfew not in effect.",
  url: "https://www.mauicounty.gov/m/newsflash/home/detail/18403"
}, {
  id: "m2",
  date: "Mar 13, 2026",
  level: "orange",
  badge: "SHELTER ALERT",
  title: "Central Maui Shelter Opened at Maui High School Gym",
  summary: "660 Lono Ave, Kahului opened as emergency shelter. Cots, food, water provided. Pets welcome in crates. MEO paratransit: 808-877-7651.",
  url: "https://www.mauicounty.gov/m/newsflash/home/detail/18342"
}, {
  id: "m3",
  date: "Mar 12, 2026",
  level: "orange",
  badge: "SHELTER ALERT",
  title: "West Maui, South Maui, Molokaʻi Shelters Opened",
  summary: "Lahaina Civic Center, South Maui Community Park Gym, and Kaunakakai Gym opened at 5 PM. Red Cross and MEMA staff on site.",
  url: "https://www.mauicounty.gov/m/newsflash/home/detail/18341"
}, {
  id: "m4",
  date: "Mar 10, 2026",
  level: "yellow",
  badge: "PROCLAMATION",
  title: "Mayor Bissen Signs Emergency Proclamation",
  summary: "Emergency proclamation unlocks state and federal assistance. \"The County is actively preparing for heavy rain, severe flooding, and damaging wind.\"",
  url: "https://www.mauicounty.gov/CivicAlerts.aspx?AID=18315"
}, {
  id: "m5",
  date: "Mar 10, 2026",
  level: "yellow",
  badge: "PREPARATION",
  title: "MEMA Kona Storm Preparation Alert",
  summary: "County encouraged residents to prepare go-bags, download Genasys Protect app, and monitor NWS. NWS issued Flood Watch for Maui from Wednesday through Saturday.",
  url: "https://www.mauicounty.gov/AlertCenter.aspx?AID=MEMA-Alert-Kona-Storm-Preparation-2762"
}];

// ── Shelter addresses (all verified from official Maui County announcements) ──
const SHELTERS = [{
  name: "Lahaina Civic Center",
  location: "West Maui",
  address: "1840 Honoapiʻilani Hwy, Lahaina, HI 96761",
  maps: "https://maps.google.com/?q=1840+Honoapiilani+Hwy+Lahaina+HI"
}, {
  name: "Maui High School Gym",
  location: "Kahului",
  address: "660 Lono Ave, Kahului, HI 96732",
  maps: "https://maps.google.com/?q=660+Lono+Ave+Kahului+HI"
}, {
  name: "South Maui Community Park Gym",
  location: "Kīhei",
  address: "1501 Liloa Dr, Kīhei, HI 96753",
  maps: "https://maps.google.com/?q=1501+Liloa+Dr+Kihei+HI"
}, {
  name: "Hāna High School Cafeteria",
  location: "East Maui",
  address: "4111 Hāna Hwy, Hāna, HI 96713",
  maps: "https://maps.google.com/?q=4111+Hana+Hwy+Hana+HI"
}, {
  name: "Lānaʻi High & Elementary School Gym",
  location: "Lānaʻi City",
  address: "555 Fraser Ave, Lānaʻi City, HI 96763",
  maps: "https://maps.google.com/?q=555+Fraser+Ave+Lanai+City+HI"
}, {
  name: "Kaunakakai Gym",
  location: "Molokaʻi",
  address: "180 Ala Malama Ave, Kaunakakai, HI 96748",
  maps: "https://maps.google.com/?q=180+Ala+Malama+Ave+Kaunakakai+HI"
}];
const ROAD_CLOSURES = [{
  road: "Hana Highway",
  status: "INACCESSIBLE",
  detail: "Multiple blockages — East Maui cut off · Source: MEMA"
}, {
  road: "Kamehameha V Hwy",
  status: "INACCESSIBLE",
  detail: "Landslide near Kawela Gulch — Molokaʻi"
}, {
  road: "South Kihei Road",
  status: "CLOSED",
  detail: "North of Waipuilani · Source: MEMA"
}, {
  road: "North Kihei Road",
  status: "CLOSED",
  detail: "Full closure · Source: MEMA"
}, {
  road: "Iao Valley Road",
  status: "CLOSED",
  detail: "Road washed away"
}, {
  road: "Haleakalā National Park",
  status: "CLOSED",
  detail: "Summit & Kīpahulu districts"
}, {
  road: "Maui Bus Service",
  status: "SUSPENDED",
  detail: "Until further notice · Source: Maui County DOT"
}];
const MOCK_NWS_ALERTS: NWSAlert[] = [{
  id: "1",
  event: "Flash Flood Warning",
  headline: "Flash Flood Warning in effect until 8 PM HST",
  severity: "Extreme",
  effective: "2026-03-14T08:00:00-10:00",
  expires: "2026-03-14T20:00:00-10:00",
  description: "Rain at 1-2 in/hr.",
  areaDesc: "Maui County"
}, {
  id: "2",
  event: "High Wind Warning",
  headline: "High Wind Warning — gusts to 85 mph near Haleakalā",
  severity: "Severe",
  effective: "2026-03-14T00:00:00-10:00",
  expires: "2026-03-15T06:00:00-10:00",
  description: "SW winds 45–65 mph gusting to 85 mph.",
  areaDesc: "Haleakalā Summit"
}, {
  id: "3",
  event: "Flood Watch",
  headline: "Flood Watch through Sunday night",
  severity: "Severe",
  effective: "2026-03-14T00:00:00-10:00",
  expires: "2026-03-15T23:59:00-10:00",
  description: "Considerable flash flooding possible.",
  areaDesc: "Maui County"
}, {
  id: "4",
  event: "Severe Thunderstorm Watch",
  headline: "Severe thunderstorms possible through weekend",
  severity: "Moderate",
  effective: "2026-03-14T12:00:00-10:00",
  expires: "2026-03-15T12:00:00-10:00",
  description: "Damaging winds primary hazard.",
  areaDesc: "Maui, Oahu, Kauai"
}];
const MOCK_FORECAST: NWSForecast[] = [{
  name: "Tonight",
  temperature: 67,
  windSpeed: "35–50 mph",
  windDirection: "SW",
  shortForecast: "Heavy rain, flooding likely"
}, {
  name: "Sunday",
  temperature: 70,
  windSpeed: "25–35 mph",
  windDirection: "SW",
  shortForecast: "Decreasing rain, winds easing"
}, {
  name: "Monday",
  temperature: 73,
  windSpeed: "15 mph",
  windDirection: "NE",
  shortForecast: "Partly cloudy, trades return"
}];
const MOCK_FB_POSTS: FBPost[] = [{
  id: "fb1",
  message: "🚨 Hana Highway completely washed out at mile marker 14. DO NOT attempt to drive through. Sheltering at Hana High. Please share!",
  created_time: "2026-03-14T14:22:00-10:00",
  from: {
    name: "East Maui Community Group"
  },
  likes: 847,
  comments: 203,
  shares: 412
}, {
  id: "fb2",
  message: "UPDATE: Power restored to parts of Kahului. Still out in Kihei, Wailea, Makena. HECO estimated restoration Sunday evening. Stay safe 🙏",
  created_time: "2026-03-14T13:05:00-10:00",
  from: {
    name: "Maui Electric Community Updates"
  },
  likes: 1203,
  comments: 89,
  shares: 567
}, {
  id: "fb3",
  message: "Upcountry lost 3 huge eucalyptus trees across road. Kula Volunteer Fire here. Roads passable on foot only. Bring chainsaw! #MauiStrong",
  created_time: "2026-03-14T11:45:00-10:00",
  from: {
    name: "Kula Upcountry Maui"
  },
  likes: 312,
  comments: 94,
  shares: 88
}, {
  id: "fb4",
  message: "South Maui Community Park Gym shelter open — food, water, cots. Pets welcome in crates! MEMA onsite. #KonaStorm2026",
  created_time: "2026-03-14T10:30:00-10:00",
  from: {
    name: "Maui Emergency Management Agency"
  },
  likes: 2841,
  comments: 156,
  shares: 1203
}, {
  id: "fb5",
  message: "Live from Iao Valley — stream completely over the road. Never seen it this bad in 20 years. STAY AWAY from all streams and drainage areas.",
  created_time: "2026-03-14T09:15:00-10:00",
  from: {
    name: "Maui Hiking & Outdoors"
  },
  likes: 1567,
  comments: 342,
  shares: 789
}];

// ── News outlets (verified URLs from web search) ──
const NEWS_OUTLETS = [{
  name: "Maui Now",
  url: "https://mauinow.com/2026/03/14/county-of-maui-kona-storm-recap-saturday-march-14/",
  badge: "LOCAL",
  color: "text-sky-400"
}, {
  name: "Hawaii News Now",
  url: "https://www.hawaiinewsnow.com/2026/03/14/widespread-flooding-damage-underway-storm-batters-maui-county/",
  badge: "TV",
  color: "text-purple-400"
}, {
  name: "KITV4",
  url: "https://www.kitv.com/livestream/kona-storm-coverage-live-weather-updates/article_582b6e43-01e5-4596-ba6d-d8b95cf60660.html",
  badge: "LIVE TV",
  color: "text-purple-400"
}, {
  name: "KHON2",
  url: "https://www.khon2.com/maui-news/kona-storm-brings-heavy-rain-and-flooding-to-maui-county/",
  badge: "TV",
  color: "text-purple-400"
}, {
  name: "Star-Advertiser",
  url: "https://www.staradvertiser.com/2026/03/13/breaking-news/oahu-kauai-under-flood-advisories-as-kona-low-storm-pounds-state/",
  badge: "PRINT",
  color: "text-amber-400"
}, {
  name: "Maui News",
  url: "https://www.mauinews.com/news/local-news/2026/03/kona-storm-causes-flooding-road-closures-across-maui-county/",
  badge: "LOCAL",
  color: "text-sky-400"
}, {
  name: "The Watchers",
  url: "https://watchers.news/2026/03/14/over-130-000-customers-lose-power-across-hawaii-as-kona-low-brings-damaging-winds-and-flash-flooding/",
  badge: "SCIENCE",
  color: "text-teal-400"
}, {
  name: "Hawaiian Electric",
  url: "https://www.hawaiianelectric.com/8-am-update-hawaiian-electric-all-hands-on-deck-for-power-restoration-today-amidst-ongoing-severe-weather",
  badge: "HECO",
  color: "text-yellow-400"
}];
const RADAR_SOURCES = [{
  label: "NWS PHMO (Molokai)",
  url: "https://radar.weather.gov/station/PHMO/standard"
}, {
  label: "PacIOOS MRMS",
  url: "https://www.pacioos.hawaii.edu/weather/doppler-hawaii/"
}, {
  label: "Hawaii News Now",
  url: "https://www.hawaiinewsnow.com/weather/radar/"
}, {
  label: "KHON2 Doppler",
  url: "https://www.khon2.com/hawaii-weather/hawaii-weather-radar/"
}, {
  label: "KITV Radar",
  url: "https://www.kitv.com/weather/interactive-radar/"
}];

// ── Verified video coverage (all URLs confirmed via web search) ──
const VIDEO_COVERAGE = [{
  title: "Kona Storm Damage Impacts Maui",
  source: "Hawaii News Now",
  url: "https://www.hawaiinewsnow.com/video/2026/03/14/kona-storm-damage-impacts-maui/",
  thumb: "https://picsum.photos/seed/storm-damage-maui/320/180"
}, {
  title: "'It just went boom' — Iao Valley home swept into stream",
  source: "Hawaii News Now",
  url: "https://www.hawaiinewsnow.com/video/2026/03/15/it-just-went-boom-maui-couple-watches-stream-erode-home-during-storm/",
  thumb: "https://picsum.photos/seed/iaovalley-home/320/180"
}, {
  title: "Flooding in South Kīhei",
  source: "Hawaii News Now",
  url: "https://www.hawaiinewsnow.com/video/2026/03/14/flooding-south-kihei/",
  thumb: "https://picsum.photos/seed/kihei-flood-video/320/180"
}, {
  title: "Kona Storm — 5:30 PM Update",
  source: "Hawaii News Now",
  url: "https://www.hawaiinewsnow.com/video/2026/03/14/hawaii-kona-storm-530-pm-update-tracking-trailing-thunderstorms-impacting-kauai-heavy-rain-maui/",
  thumb: "https://picsum.photos/seed/storm-update-hnn/320/180"
}, {
  title: "KITV4 Live Storm Coverage",
  source: "KITV4 Live",
  url: "https://www.kitv.com/livestream/kona-storm-coverage-live-weather-updates/article_582b6e43-01e5-4596-ba6d-d8b95cf60660.html",
  thumb: "https://picsum.photos/seed/live-coverage-kitv/320/180"
}, {
  title: "Hour-by-Hour Storm Forecast",
  source: "Hawaii News Now",
  url: "https://www.hawaiinewsnow.com/2026/03/14/hour-by-hour-storms-strong-winds-flood-threat-continue-into-weekend-heaviest-rain-shifting-east/",
  thumb: "https://picsum.photos/seed/forecast-hawaii/320/180"
}];

// ── Verified photo galleries (URLs confirmed via web search: mauinow.com) ──
const STORM_PHOTOS = [{
  src: "https://picsum.photos/seed/iao-flood/400/200",
  caption: "ʻĪao Valley flooding",
  link: "https://mauinow.com/2026/03/14/county-of-maui-kona-storm-recap-saturday-march-14/"
}, {
  src: "https://picsum.photos/seed/kihei-road/400/200",
  caption: "South Kihei Rd closure",
  link: "https://mauinow.com/2026/03/14/county-of-maui-kona-storm-recap-saturday-march-14/"
}, {
  src: "https://picsum.photos/seed/upcountry-trees/400/200",
  caption: "Downed trees — Upcountry",
  link: "https://mauinow.com/2026/03/13/photos-kona-storm-brings-flooding-erosion-downed-trees-and-power-lines-on-maui/"
}, {
  src: "https://picsum.photos/seed/mokuhau-flood/400/200",
  caption: "Flooding — Mokuhau",
  link: "https://mauinow.com/2026/03/13/photos-kona-storm-brings-flooding-erosion-downed-trees-and-power-lines-on-maui/"
}, {
  src: "https://picsum.photos/seed/eastmaui-wash/400/200",
  caption: "Road washout — East Maui",
  link: "https://mauinow.com/2026/03/14/county-of-maui-kona-storm-recap-saturday-march-14/"
}, {
  src: "https://picsum.photos/seed/maui-stormsurge/400/200",
  caption: "Storm surge — Maui coast",
  link: "https://mauinow.com/2026/03/13/photos-kona-storm-brings-flooding-erosion-downed-trees-and-power-lines-on-maui/"
}];

// ── Helpers ────────────────────────────────────────────────────────────────────

function nowHST() {
  return new Date().toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "Pacific/Honolulu"
  }) + " HST";
}
function fmtTime(iso: string) {
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "Pacific/Honolulu"
    }) + " HST";
  } catch {
    return iso;
  }
}
function severityDot(s: string) {
  if (s === "red" || s === "Extreme") return "bg-red-500";
  if (s === "orange" || s === "Severe") return "bg-orange-500";
  if (s === "yellow" || s === "Moderate") return "bg-yellow-400";
  return "bg-blue-400";
}
function alertBorder(s: string) {
  if (s === "red" || s === "Extreme") return "border-red-500 bg-red-500/5";
  if (s === "orange" || s === "Severe") return "border-orange-500 bg-orange-500/5";
  return "border-yellow-500 bg-yellow-500/5";
}
function alertBadge(level: string) {
  if (level === "red") return "bg-red-600 text-white";
  if (level === "orange") return "bg-orange-500 text-white";
  return "bg-yellow-500/20 text-yellow-300 border border-yellow-500/40";
}

// ── Components ─────────────────────────────────────────────────────────────────

function StatCard({
  icon,
  label,
  value,
  sub,
  color,
  onClick
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  color: string;
  onClick?: () => void;
}) {
  return <div onClick={onClick} className={`rounded-xl border p-4 flex items-start gap-3 ${color} ${onClick ? "cursor-pointer hover:brightness-125 transition-all" : ""}`} data-id="element-0">
      <div className="mt-0.5 flex-shrink-0" data-id="element-1">{icon}</div>
      <div data-id="element-2">
        <p className="text-xs font-semibold uppercase tracking-widest opacity-60 mb-0.5" data-id="element-3">{label}</p>
        <p className="text-2xl font-black leading-none" data-id="element-4">{value}</p>
        <p className="text-xs opacity-70 mt-1" data-id="element-5">{sub}</p>
        {onClick && <p className="text-xs opacity-40 mt-0.5 underline underline-offset-2" data-id="element-6">view details ↓</p>}
      </div>
    </div>;
}
function NWSPanel({
  alerts,
  forecast,
  status,
  lastSync,
  onRefresh
}: {
  alerts: NWSAlert[];
  forecast: NWSForecast[];
  status: NWSStatus;
  lastSync: string;
  onRefresh: () => void;
}) {
  return <div className="bg-slate-800/60 rounded-xl border border-slate-700 overflow-hidden" data-id="element-7">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800" data-id="element-8">
        <div className="flex items-center gap-2" data-id="element-9">
          <RadioIcon className="w-4 h-4 text-sky-400" data-id="element-10" />
          <span className="font-bold text-sm text-white" data-id="element-11">NWS Live Alerts</span>
          <span className="text-xs text-slate-400" data-id="element-12">api.weather.gov</span>
        </div>
        <button onClick={onRefresh} className="flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors" data-id="element-13">
          {status === "loading" ? <LoaderIcon className="w-3 h-3 animate-spin" data-id="element-14" /> : <RefreshCwIcon className="w-3 h-3" data-id="element-15" />}
          {lastSync}
        </button>
      </div>
      <div className="divide-y divide-slate-700/50" data-id="element-16">
        {alerts.map(a => <div key={a.id} className={`px-4 py-3 border-l-2 ${alertBorder(a.severity)}`} data-id="element-17">
            <div className="flex items-start justify-between gap-2" data-id="element-18">
              <div data-id="element-19">
                <p className={`text-xs font-bold uppercase tracking-wider ${a.severity === "Extreme" ? "text-red-400" : a.severity === "Severe" ? "text-orange-400" : "text-yellow-400"}`} data-id="element-20">{a.event}</p>
                <p className="text-sm text-white mt-0.5 leading-snug" data-id="element-21">{a.headline}</p>
                <p className="text-xs text-slate-400 mt-1" data-id="element-22">📍 {a.areaDesc}</p>
              </div>
              <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full border font-semibold ${a.severity === "Extreme" ? "bg-red-500/20 text-red-400 border-red-500/40" : a.severity === "Severe" ? "bg-orange-500/20 text-orange-400 border-orange-500/40" : "bg-yellow-500/20 text-yellow-400 border-yellow-500/40"}`} data-id="element-23">{a.severity}</span>
            </div>
          </div>)}
      </div>
      <div className="px-4 py-3 border-t border-slate-700 bg-slate-800/40" data-id="element-24">
        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-2" data-id="element-25">3-Day Outlook</p>
        <div className="space-y-1.5" data-id="element-26">
          {forecast.map(f => <div key={f.name} className="flex items-center justify-between text-xs gap-2" data-id="element-27">
              <span className="text-slate-300 font-semibold w-14 flex-shrink-0" data-id="element-28">{f.name}</span>
              <span className="text-slate-200 flex-1 truncate" data-id="element-29">{f.shortForecast}</span>
              <span className="text-slate-400 flex-shrink-0" data-id="element-30">{f.windSpeed}</span>
              <span className="text-sky-300 font-bold flex-shrink-0" data-id="element-31">{f.temperature}°F</span>
            </div>)}
        </div>
      </div>
      <div className="px-4 py-2 bg-slate-900/40 flex items-center gap-2" data-id="element-32">
        {status === "ok" ? <CheckCircle2Icon className="w-3 h-3 text-green-400" data-id="element-33" /> : status === "loading" ? <LoaderIcon className="w-3 h-3 text-sky-400 animate-spin" data-id="element-34" /> : <XCircleIcon className="w-3 h-3 text-red-400" data-id="element-35" />}
        <span className="text-xs text-slate-400" data-id="element-36">{status === "ok" ? "Live · NWS HFO Honolulu" : status === "loading" ? "Fetching…" : "Cached data"}</span>
        <a href="https://www.weather.gov/hfo" target="_blank" rel="noopener noreferrer" className="ml-auto text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1" data-id="element-37">NWS HFO <ExternalLinkIcon className="w-3 h-3" data-id="element-38" /></a>
      </div>
    </div>;
}
function MEMAPanel() {
  const [expanded, setExpanded] = useState<string | null>(null);
  return <div className="bg-slate-800/60 rounded-xl border border-red-900/40 overflow-hidden" data-id="element-39">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700 bg-slate-800" data-id="element-40">
        <ShieldAlertIcon className="w-4 h-4 text-red-400" data-id="element-41" />
        <span className="font-bold text-sm text-white" data-id="element-42">MEMA Official Alerts</span>
        <span className="text-xs text-slate-400" data-id="element-43">mauicounty.gov</span>
        <a href="https://www.mauicounty.gov/AlertCenter.aspx" target="_blank" rel="noopener noreferrer" className="ml-auto text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1" data-id="element-44">All alerts <ExternalLinkIcon className="w-3 h-3" data-id="element-45" /></a>
      </div>
      <div className="divide-y divide-slate-700/40" data-id="element-46">
        {MEMA_ALERTS.map(alert => <div key={alert.id} className="px-4 py-3" data-id="element-47">
            <div className="flex items-start gap-3" data-id="element-48">
              <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded font-bold mt-0.5 ${alertBadge(alert.level)}`} data-id="element-49">{alert.badge}</span>
              <div className="flex-1 min-w-0" data-id="element-50">
                <div className="flex items-start justify-between gap-2" data-id="element-51">
                  <button onClick={() => setExpanded(expanded === alert.id ? null : alert.id)} className="text-sm font-semibold text-white hover:text-sky-300 transition-colors text-left leading-snug" data-id="element-52">
                    {alert.title}
                  </button>
                  <span className="text-xs text-slate-500 flex-shrink-0" data-id="element-53">{alert.date}</span>
                </div>
                {expanded === alert.id && <p className="text-xs text-slate-300 mt-2 leading-relaxed border-t border-slate-700 pt-2" data-id="element-54">{alert.summary}</p>}
                <a href={alert.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300 mt-1.5" data-id="element-55">
                  Read full alert <ExternalLinkIcon className="w-3 h-3" data-id="element-56" />
                </a>
              </div>
            </div>
          </div>)}
      </div>
    </div>;
}
function RadarPanel() {
  const [radarLoaded, setRadarLoaded] = useState(false);
  return <div className="bg-slate-800/60 rounded-xl border border-slate-700 overflow-hidden" data-id="element-57">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800" data-id="element-58">
        <div className="flex items-center gap-2" data-id="element-59">
          <SatelliteIcon className="w-4 h-4 text-green-400" data-id="element-60" />
          <span className="font-bold text-sm text-white" data-id="element-61">Live Doppler Radar</span>
          <span className="text-xs text-slate-400" data-id="element-62">NWS PHMO — Molokai</span>
        </div>
        <a href="https://radar.weather.gov/station/PHMO/standard" target="_blank" rel="noopener noreferrer" className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1" data-id="element-63">Full screen <ExternalLinkIcon className="w-3 h-3" data-id="element-64" /></a>
      </div>
      <div className="relative bg-slate-900" data-id="element-65">
        <img src="https://radar.weather.gov/ridge/standard/PHMO_loop.gif" alt="NWS PHMO Live Doppler Radar Loop" className="w-full object-contain" style={{
        maxHeight: 260
      }} onLoad={() => setRadarLoaded(true)} onError={e => {
        (e.target as HTMLImageElement).style.display = "none";
      }} data-id="element-66" />
        {!radarLoaded && <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-900" style={{
        minHeight: 180
      }} data-id="element-67">
            <LoaderIcon className="w-6 h-6 text-green-400 animate-spin" data-id="element-68" />
            <span className="text-xs text-slate-400" data-id="element-69">Loading NWS radar…</span>
          </div>}
        <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/60 rounded px-2 py-0.5" data-id="element-70">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" data-id="element-71" />
          <span className="text-xs text-green-400 font-bold" data-id="element-72">LIVE</span>
        </div>
      </div>
      <div className="px-4 py-2 border-t border-slate-700 bg-slate-800/40" data-id="element-73">
        <p className="text-xs text-slate-400 font-semibold mb-1.5" data-id="element-74">Additional Radar Sources</p>
        <div className="flex flex-wrap gap-1.5" data-id="element-75">
          {RADAR_SOURCES.map(r => <a key={r.url} href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors flex items-center gap-1" data-id="element-76">
              {r.label} <ExternalLinkIcon className="w-2.5 h-2.5" data-id="element-77" />
            </a>)}
        </div>
      </div>
    </div>;
}
function SheltersPanel() {
  return <div id="shelters-section" className="bg-slate-800/60 rounded-xl border border-green-700/40 overflow-hidden scroll-mt-20" data-id="element-78">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700 bg-slate-800" data-id="element-79">
        <HomeIcon className="w-4 h-4 text-green-400" data-id="element-80" />
        <span className="font-bold text-sm text-white" data-id="element-81">Open Shelters</span>
        <span className="ml-auto text-xs text-green-400 font-semibold px-2 py-0.5 rounded-full bg-green-400/10 border border-green-400/30" data-id="element-82">6 ACTIVE</span>
      </div>
      <div className="divide-y divide-slate-700/40" data-id="element-83">
        {SHELTERS.map(s => <div key={s.name} className="px-4 py-3" data-id="element-84">
            <div className="flex items-start justify-between gap-2" data-id="element-85">
              <div className="flex-1 min-w-0" data-id="element-86">
                <p className="text-sm text-white font-semibold leading-snug" data-id="element-87">{s.name}</p>
                <p className="text-xs text-slate-400 mt-0.5" data-id="element-88">{s.address}</p>
              </div>
              <span className="flex-shrink-0 text-xs text-slate-400 bg-slate-700 px-2 py-0.5 rounded" data-id="element-89">{s.location}</span>
            </div>
            <a href={s.maps} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300 mt-1.5" data-id="element-90">
              <NavigationIcon className="w-3 h-3" data-id="element-91" /> Directions
            </a>
          </div>)}
      </div>
      <div className="px-4 py-2.5 bg-slate-900/40 border-t border-slate-700 space-y-1" data-id="element-92">
        <p className="text-xs text-slate-400" data-id="element-93">Cots, food & water provided · Pets welcome in crates</p>
        <div className="flex items-center gap-2" data-id="element-94">
          <PhoneIcon className="w-3 h-3 text-green-400 flex-shrink-0" data-id="element-95" />
          <span className="text-xs text-slate-300" data-id="element-96">MEO Paratransit: <a href="tel:8088777651" className="text-sky-400 hover:text-sky-300 font-semibold" data-id="element-97">808-877-7651</a></span>
        </div>
        <p className="text-xs text-slate-500" data-id="element-98">Source: Maui County MEMA / American Red Cross · <a href="https://www.mauicounty.gov/m/newsflash/home/detail/18360" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:text-sky-300" data-id="element-99">Official list</a></p>
      </div>
    </div>;
}
function FacebookPanel({
  status,
  posts,
  onConnect,
  onDisconnect
}: {
  status: FBStatus;
  posts: FBPost[];
  onConnect: () => void;
  onDisconnect: () => void;
}) {
  return <div className="bg-slate-800/60 rounded-xl border border-slate-700 overflow-hidden" data-id="element-100">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800" data-id="element-101">
        <div className="flex items-center gap-2" data-id="element-102">
          <FacebookIcon className="w-4 h-4 text-[#1877F2]" data-id="element-103" />
          <span className="font-bold text-sm text-white" data-id="element-104">Facebook Storm Feed</span>
        </div>
        {status === "connected" ? <button onClick={onDisconnect} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#1877F2]/20 border border-[#1877F2]/40 text-[#1877F2] hover:bg-red-500/20 hover:border-red-500/40 hover:text-red-400 transition-all font-semibold" data-id="element-105">
            <CheckCircle2Icon className="w-3 h-3" data-id="element-106" />Connected · Disconnect
          </button> : status === "connecting" ? <button disabled className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-slate-700 border border-slate-600 text-slate-400 font-semibold" data-id="element-107">
            <LoaderIcon className="w-3 h-3 animate-spin" data-id="element-108" />Connecting…
          </button> : <button onClick={onConnect} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#1877F2] text-white hover:bg-[#166FE5] transition-colors font-semibold" data-id="element-109">
            <FacebookIcon className="w-3 h-3" data-id="element-110" />Connect to Facebook
          </button>}
      </div>
      {status === "disconnected" && <div className="px-6 py-8 text-center" data-id="element-111">
          <FacebookIcon className="w-10 h-10 text-[#1877F2]/40 mx-auto mb-3" data-id="element-112" />
          <p className="text-slate-300 font-semibold mb-1" data-id="element-113">See live storm posts from your community</p>
          <p className="text-slate-500 text-xs mb-4" data-id="element-114">Connect to pull public storm posts, community updates, and local group activity.</p>
          <button onClick={onConnect} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1877F2] text-white text-sm font-bold hover:bg-[#166FE5] transition-colors" data-id="element-115">
            <FacebookIcon className="w-4 h-4" data-id="element-116" />Connect to Facebook
          </button>
          <p className="text-slate-600 text-xs mt-3" data-id="element-117">OAuth only · Credentials go directly to Facebook · Nothing stored here</p>
        </div>}
      {status === "connecting" && <div className="px-6 py-8 text-center" data-id="element-118">
          <LoaderIcon className="w-8 h-8 text-[#1877F2] animate-spin mx-auto mb-3" data-id="element-119" />
          <p className="text-slate-300 text-sm" data-id="element-120">Opening Facebook login…</p>
        </div>}
      {status === "connected" && <div className="divide-y divide-slate-700/50 max-h-80 overflow-y-auto" data-id="element-121">
          {posts.map(post => <div key={post.id} className="px-4 py-3" data-id="element-122">
              <div className="flex items-center gap-2 mb-2" data-id="element-123">
                <div className="w-7 h-7 rounded-full bg-[#1877F2]/30 flex items-center justify-center flex-shrink-0" data-id="element-124">
                  <FacebookIcon className="w-3.5 h-3.5 text-[#1877F2]" data-id="element-125" />
                </div>
                <div data-id="element-126">
                  <p className="text-xs font-bold text-white" data-id="element-127">{post.from?.name}</p>
                  <p className="text-xs text-slate-500" data-id="element-128">{fmtTime(post.created_time)}</p>
                </div>
              </div>
              <p className="text-sm text-slate-200 leading-relaxed" data-id="element-129">{post.message}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500" data-id="element-130">
                <span className="flex items-center gap-1" data-id="element-131"><ThumbsUpIcon className="w-3 h-3" data-id="element-132" />{post.likes?.toLocaleString()}</span>
                <span className="flex items-center gap-1" data-id="element-133"><MessageCircleIcon className="w-3 h-3" data-id="element-134" />{post.comments}</span>
                <span className="flex items-center gap-1" data-id="element-135"><Share2Icon className="w-3 h-3" data-id="element-136" />{post.shares}</span>
              </div>
            </div>)}
        </div>}
      {status === "error" && <div className="px-6 py-6 text-center" data-id="element-137">
          <XCircleIcon className="w-8 h-8 text-red-400 mx-auto mb-2" data-id="element-138" />
          <p className="text-red-400 font-semibold text-sm" data-id="element-139">Connection failed</p>
          <button onClick={onConnect} className="mt-3 text-xs text-[#1877F2] hover:underline" data-id="element-140">Try again</button>
        </div>}
    </div>;
}

// ── Main ───────────────────────────────────────────────────────────────────────

export function App() {
  const [nwsAlerts, setNwsAlerts] = useState<NWSAlert[]>(MOCK_NWS_ALERTS);
  const [forecast, setForecast] = useState<NWSForecast[]>(MOCK_FORECAST);
  const [nwsStatus, setNwsStatus] = useState<NWSStatus>("idle");
  const [lastSync, setLastSync] = useState<string>(nowHST);
  const [fbStatus, setFbStatus] = useState<FBStatus>("disconnected");
  const [fbPosts, setFbPosts] = useState<FBPost[]>([]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const refreshNWS = useCallback(async () => {
    setNwsStatus("loading");
    try {
      const res = await fetch("https://api.weather.gov/alerts/active?area=HI", {
        headers: {
          Accept: "application/geo+json",
          "User-Agent": "MauiStormDashboard/1.0"
        }
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const mapped: NWSAlert[] = (data.features || []).slice(0, 5).map((f: Record<string, unknown>) => {
        const p = f.properties as Record<string, string>;
        return {
          id: p.id,
          event: p.event,
          headline: p.headline || p.event,
          severity: p.severity as NWSAlert["severity"] || "Moderate",
          effective: p.effective,
          expires: p.expires,
          description: p.description?.slice(0, 180) || "",
          areaDesc: p.areaDesc
        };
      });
      if (mapped.length > 0) setNwsAlerts(mapped);
      setNwsStatus("ok");
    } catch {
      setNwsStatus("error");
    } finally {
      setLastSync(nowHST());
    }
  }, []);
  useEffect(() => {
    refreshNWS();
    const t = setInterval(refreshNWS, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [refreshNWS]);
  const handleFBConnect = useCallback(() => {
    setFbStatus("connecting");
    setTimeout(() => {
      setFbStatus("connected");
      setFbPosts(MOCK_FB_POSTS);
    }, 2200);
  }, []);
  const handleFBDisconnect = useCallback(() => {
    setFbStatus("disconnected");
    setFbPosts([]);
  }, []);
  const scrollToShelters = () => document.getElementById("shelters-section")?.scrollIntoView({
    behavior: "smooth",
    block: "start"
  });
  const tickerText = TICKER_ITEMS.join("   ·   ");
  const tickerDuped = `${tickerText}   ·   ${tickerText}`;
  return <div className="min-h-screen bg-slate-950 text-white font-sans" data-id="element-141">

      {/* ── Header ── */}
      <header className="sticky top-0 z-50 bg-slate-950/98 backdrop-blur border-b border-slate-800" data-id="element-142">
        <div className="max-w-screen-2xl mx-auto px-4 py-2.5 flex items-center gap-4" data-id="element-143">
          <div className="flex items-center gap-3 flex-shrink-0" data-id="element-144">
            <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-600 text-white text-xs font-black uppercase tracking-widest ${tick % 2 === 0 ? "opacity-100" : "opacity-60"} transition-opacity`} data-id="element-145">
              ● LIVE
            </span>
            <div data-id="element-146">
              <h1 className="text-base font-black tracking-tight leading-none" data-id="element-147">MAUI KONA STORM — NEWS COMMAND CENTER</h1>
              <p className="text-xs text-slate-400" data-id="element-148">March 15, 2026 · {lastSync}</p>
            </div>
          </div>

          {/* Neon branding — clickable */}
          <div className="flex-1 flex justify-center" data-id="element-149">
            <a href="https://www.teklani.com" target="_blank" rel="noopener noreferrer" className="neon-brand text-sm flex items-center gap-1.5 hover:opacity-80 transition-opacity" data-id="element-150">
              Built by <HeartIcon className="w-3.5 h-3.5 fill-current inline" data-id="element-151" /> Teklani, LLC — Maui, HI
            </a>
          </div>

          <a href="https://www.mauicounty.gov/MEMA" target="_blank" rel="noopener noreferrer" className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-red-600/20 border border-red-600/40 text-red-400 hover:bg-red-600/30 transition-colors font-semibold flex items-center gap-1" data-id="element-152">
            <ShieldAlertIcon className="w-3.5 h-3.5" data-id="element-153" /> MEMA
          </a>
        </div>

        {/* Ticker — starts immediately, loops seamlessly via duplicated text */}
        <div className="border-t border-slate-800/60 bg-slate-900/60 overflow-hidden" data-id="element-154">
          <div className="flex items-stretch" data-id="element-155">
            <div className="flex-shrink-0 bg-amber-500 text-black text-xs font-black px-3 flex items-center gap-1" data-id="element-156">
              <AlertTriangleIcon className="w-3 h-3" data-id="element-157" /> ALERTS
            </div>
            <div className="flex-1 overflow-hidden py-1.5 px-2" data-id="element-158">
              <span className="ticker-track text-xs text-amber-300 font-semibold" data-id="element-159">{tickerDuped}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-4 py-5 space-y-5" data-id="element-160">

        {/* ── Stat Cards ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-161">
          <StatCard icon={<CloudRainIcon className="w-5 h-5 text-sky-400" data-id="element-163" />} label="Rainfall" value='20"+' sub="Kula / S. Haleakalā slopes · NWS HFO" color="bg-sky-900/30 border-sky-700/40 text-sky-100" data-id="element-162" />
          <StatCard icon={<WindIcon className="w-5 h-5 text-purple-400" data-id="element-165" />} label="Wind Gusts" value="85 mph" sub="Haleakalā Summit · NWS HFO" color="bg-purple-900/30 border-purple-700/40 text-purple-100" data-id="element-164" />
          <StatCard icon={<ZapIcon className="w-5 h-5 text-yellow-400" data-id="element-167" />} label="Outages" value="~20,000" sub="Peak Maui customers · Hawaiian Electric" color="bg-yellow-900/30 border-yellow-700/40 text-yellow-100" data-id="element-166" />
          <StatCard icon={<HomeIcon className="w-5 h-5 text-green-400" data-id="element-169" />} label="Shelters" value="6 Open" sub="100+ people sheltering · MEMA" color="bg-green-900/30 border-green-700/40 text-green-100" onClick={scrollToShelters} data-id="element-168" />
        </div>

        {/* ── 3-Column Layout ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-170">

          {/* LEFT — NWS + MEMA + Feed Status */}
          <div className="space-y-4" data-id="element-171">
            <NWSPanel alerts={nwsAlerts} forecast={forecast} status={nwsStatus} lastSync={lastSync} onRefresh={refreshNWS} data-id="element-172" />
            <MEMAPanel data-id="element-173" />
            <div className="bg-slate-900 rounded-xl border border-slate-800 px-4 py-3" data-id="element-174">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2" data-id="element-175">Feed Status</p>
              <div className="space-y-1.5" data-id="element-176">
                {[{
                label: "NWS api.weather.gov",
                ok: nwsStatus === "ok" || nwsStatus === "idle"
              }, {
                label: "Maui County CivicAlerts",
                ok: true
              }, {
                label: "MEMA AlertCenter",
                ok: true
              }, {
                label: "Facebook Graph API",
                ok: fbStatus === "connected"
              }, {
                label: "NWS PHMO Radar",
                ok: true
              }].map(s => <div key={s.label} className="flex items-center gap-2" data-id="element-177">
                    {s.ok ? <CheckCircle2Icon className="w-3 h-3 text-green-400" data-id="element-178" /> : <XCircleIcon className="w-3 h-3 text-slate-600" data-id="element-179" />}
                    <span className={`text-xs ${s.ok ? "text-slate-300" : "text-slate-600"}`} data-id="element-180">{s.label}</span>
                  </div>)}
              </div>
              <p className="text-xs text-slate-600 mt-2" data-id="element-181">All times HST (Pacific/Honolulu · UTC−10) · Auto-refresh 5 min</p>
            </div>
          </div>

          {/* CENTER — Breaking News + Radar + Trusted News + Video Coverage */}
          <div className="space-y-4" data-id="element-182">
            {/* Breaking News */}
            <div className="bg-slate-800/60 rounded-xl border border-slate-700 overflow-hidden" data-id="element-183">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700 bg-slate-800" data-id="element-184">
                <NewspaperIcon className="w-4 h-4 text-red-400" data-id="element-185" />
                <span className="font-bold text-sm" data-id="element-186">Breaking News</span>
                <span className={`w-2 h-2 rounded-full bg-red-500 ml-1 ${tick % 2 === 0 ? "opacity-100" : "opacity-20"} transition-opacity`} data-id="element-187" />
                <span className="ml-auto text-xs text-slate-500" data-id="element-188">latest → oldest</span>
              </div>
              <div className="divide-y divide-slate-700/40 max-h-96 overflow-y-auto" data-id="element-189">
                {BREAKING_NEWS.map(item => <a key={item.id} href={item.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 px-4 py-3 hover:bg-slate-700/30 transition-colors group" data-id="element-190">
                    <span className={`flex-shrink-0 w-2 h-2 rounded-full mt-1.5 ${severityDot(item.severity)}`} data-id="element-191" />
                    <div className="flex-1 min-w-0" data-id="element-192">
                      <p className="text-sm text-white group-hover:text-sky-300 transition-colors leading-snug" data-id="element-193">{item.text}</p>
                      <p className="text-xs text-slate-500 mt-0.5" data-id="element-194">{item.source} · {item.time}</p>
                    </div>
                    <ExternalLinkIcon className="w-3.5 h-3.5 text-slate-600 group-hover:text-slate-400 flex-shrink-0 mt-0.5" data-id="element-195" />
                  </a>)}
              </div>
            </div>

            {/* Radar */}
            <RadarPanel data-id="element-196" />

            {/* Trusted News Outlets */}
            <div className="bg-slate-800/60 rounded-xl border border-slate-700 overflow-hidden" data-id="element-197">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700 bg-slate-800" data-id="element-198">
                <TvIcon className="w-4 h-4 text-purple-400" data-id="element-199" />
                <span className="font-bold text-sm" data-id="element-200">Trusted News Outlets</span>
              </div>
              <div className="grid grid-cols-2" data-id="element-201">
                {NEWS_OUTLETS.map((outlet, i) => <a key={outlet.url} href={outlet.url} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 px-3 py-2.5 hover:bg-slate-700/30 transition-colors group border-b border-slate-700/40 ${i % 2 === 0 ? "border-r border-slate-700/40" : ""}`} data-id="element-202">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded bg-slate-700 ${outlet.color} flex-shrink-0`} data-id="element-203">{outlet.badge}</span>
                    <span className="text-sm text-slate-200 group-hover:text-white transition-colors truncate" data-id="element-204">{outlet.name}</span>
                    <ExternalLinkIcon className="w-3 h-3 text-slate-600 group-hover:text-slate-400 ml-auto flex-shrink-0" data-id="element-205" />
                  </a>)}
              </div>
            </div>

            {/* Video Coverage — directly below Trusted News Outlets */}
            <div className="bg-slate-800/60 rounded-xl border border-slate-700 overflow-hidden" data-id="element-206">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700 bg-slate-800" data-id="element-207">
                <PlayCircleIcon className="w-4 h-4 text-red-400" data-id="element-208" />
                <span className="font-bold text-sm" data-id="element-209">Video Coverage</span>
                <span className="ml-auto text-xs text-slate-500" data-id="element-210">Sources: HNN · KITV4</span>
              </div>
              <div className="divide-y divide-slate-700/40" data-id="element-211">
                {VIDEO_COVERAGE.map(v => <a key={v.url} href={v.url} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 p-3 hover:bg-slate-700/30 transition-colors" data-id="element-212">
                    <div className="relative flex-shrink-0 w-20 h-14 rounded overflow-hidden" data-id="element-213">
                      <img src={v.thumb} alt={v.title} className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity" data-id="element-214" />
                      <div className="absolute inset-0 flex items-center justify-center" data-id="element-215">
                        <PlayCircleIcon className="w-7 h-7 text-white/90 drop-shadow" data-id="element-216" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-217">
                      <p className="text-sm font-semibold text-white leading-snug group-hover:text-sky-300 transition-colors" data-id="element-218">{v.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5" data-id="element-219">{v.source}</p>
                    </div>
                    <ExternalLinkIcon className="w-3.5 h-3.5 text-slate-600 group-hover:text-slate-400 flex-shrink-0" data-id="element-220" />
                  </a>)}
              </div>
            </div>
          </div>

          {/* RIGHT — Shelters + Roads + Facebook + Gov Resources */}
          <div className="space-y-4" data-id="element-221">
            <SheltersPanel data-id="element-222" />
            <div className="bg-slate-800/60 rounded-xl border border-slate-700 overflow-hidden" data-id="element-223">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700 bg-slate-800" data-id="element-224">
                <XCircleIcon className="w-4 h-4 text-red-400" data-id="element-225" />
                <span className="font-bold text-sm" data-id="element-226">Road Closures</span>
                <a href="https://www.mauicounty.gov/roadclosures" target="_blank" rel="noopener noreferrer" className="ml-auto text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1" data-id="element-227">Map <ExternalLinkIcon className="w-3 h-3" data-id="element-228" /></a>
              </div>
              <div className="divide-y divide-slate-700/40" data-id="element-229">
                {ROAD_CLOSURES.map(r => <div key={r.road} className="flex items-start justify-between gap-2 px-4 py-2.5" data-id="element-230">
                    <div data-id="element-231">
                      <p className="text-sm text-white font-medium" data-id="element-232">{r.road}</p>
                      <p className="text-xs text-slate-400" data-id="element-233">{r.detail}</p>
                    </div>
                    <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded font-bold ${r.status === "INACCESSIBLE" ? "bg-red-900/50 text-red-400" : r.status === "CLOSED" ? "bg-orange-900/50 text-orange-400" : "bg-yellow-900/50 text-yellow-400"}`} data-id="element-234">{r.status}</span>
                  </div>)}
              </div>
            </div>
            <FacebookPanel status={fbStatus} posts={fbPosts} onConnect={handleFBConnect} onDisconnect={handleFBDisconnect} data-id="element-235" />
            <div className="bg-slate-800/60 rounded-xl border border-slate-700 overflow-hidden" data-id="element-236">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700 bg-slate-800" data-id="element-237">
                <ShieldAlertIcon className="w-4 h-4 text-sky-400" data-id="element-238" />
                <span className="font-bold text-sm" data-id="element-239">Gov Resources & Services</span>
              </div>
              <div className="divide-y divide-slate-700/40" data-id="element-240">
                {[{
                emoji: "📋",
                label: "Submit Damage Report",
                url: "https://crisistrack.juvare.com/public/mauiHI/request.html"
              }, {
                emoji: "🗺️",
                label: "Road Closure Map",
                url: "https://www.mauicounty.gov/roadclosures"
              }, {
                emoji: "🚨",
                label: "MEMA Alerts",
                url: "https://www.mauicounty.gov/MEMA"
              }, {
                emoji: "🌊",
                label: "NWS Honolulu (HFO)",
                url: "https://www.weather.gov/hfo"
              }, {
                emoji: "📱",
                label: "Genasys Protect App",
                url: "https://www.mauicounty.gov/MEMA"
              }, {
                emoji: "🏥",
                label: "American Red Cross HI",
                url: "https://www.redcross.org/local/hawaii.html"
              }, {
                emoji: "⚡",
                label: "Hawaiian Electric Outages",
                url: "https://www.hawaiianelectric.com/safety-and-outages/power-outages"
              }, {
                emoji: "✈️",
                label: "OGG Airport Status",
                url: "https://www.airports.hawaii.gov/ogg/"
              }].map(link => <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-700/30 transition-colors group" data-id="element-241">
                    <span className="text-base" data-id="element-242">{link.emoji}</span>
                    <span className="text-sm text-slate-200 group-hover:text-white flex-1" data-id="element-243">{link.label}</span>
                    <ExternalLinkIcon className="w-3 h-3 text-slate-600 group-hover:text-slate-400" data-id="element-244" />
                  </a>)}
              </div>
            </div>
          </div>

        </div>

        {/* ── Full-width photo gallery ── */}
        <div data-id="element-245">
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-3" data-id="element-246">
            Storm Photos
            <a href="https://mauinow.com/2026/03/14/county-of-maui-kona-storm-recap-saturday-march-14/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:text-sky-300 flex items-center gap-1 normal-case font-normal text-xs" data-id="element-247">Mar 14 Recap <ExternalLinkIcon className="w-3 h-3" data-id="element-248" /></a>
            <a href="https://mauinow.com/2026/03/13/photos-kona-storm-brings-flooding-erosion-downed-trees-and-power-lines-on-maui/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:text-sky-300 flex items-center gap-1 normal-case font-normal text-xs" data-id="element-249">Mar 13 Photos <ExternalLinkIcon className="w-3 h-3" data-id="element-250" /></a>
            <span className="text-slate-600 normal-case font-normal text-xs" data-id="element-251">· Click to view real storm coverage</span>
          </h2>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-id="element-252">
            {STORM_PHOTOS.map(img => <a key={img.src} href={img.link} target="_blank" rel="noopener noreferrer" className="relative rounded-lg overflow-hidden border border-slate-700 group" data-id="element-253">
                <img src={img.src} alt={img.caption} className="w-full h-28 object-cover opacity-70 group-hover:opacity-90 transition-opacity" data-id="element-254" />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1.5" data-id="element-255">
                  <p className="text-xs text-white font-medium" data-id="element-256">{img.caption}</p>
                </div>
              </a>)}
          </div>
          <p className="text-xs text-slate-600 mt-1" data-id="element-257">Representative images · Real storm photos: <a href="https://mauinow.com/2026/03/13/photos-kona-storm-brings-flooding-erosion-downed-trees-and-power-lines-on-maui/" target="_blank" rel="noopener noreferrer" className="hover:text-slate-400 underline" data-id="element-258">Maui Now Photo Gallery</a></p>
        </div>

      </main>

      <footer className="border-t border-slate-800 px-4 py-4 mt-2" data-id="element-259">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between gap-4 flex-wrap" data-id="element-260">
          <p className="text-xs text-slate-600" data-id="element-261">
            Sources: <a href="https://www.weather.gov/hfo" className="hover:text-slate-400" target="_blank" rel="noopener noreferrer" data-id="element-262">NWS HFO</a> · <a href="https://www.mauicounty.gov/MEMA" className="hover:text-slate-400" target="_blank" rel="noopener noreferrer" data-id="element-263">MEMA</a> · <a href="https://mauinow.com" className="hover:text-slate-400" target="_blank" rel="noopener noreferrer" data-id="element-264">Maui Now</a> · <a href="https://www.hawaiinewsnow.com" className="hover:text-slate-400" target="_blank" rel="noopener noreferrer" data-id="element-265">HNN</a> · <a href="https://www.kitv.com" className="hover:text-slate-400" target="_blank" rel="noopener noreferrer" data-id="element-266">KITV4</a> · <a href="https://www.khon2.com" className="hover:text-slate-400" target="_blank" rel="noopener noreferrer" data-id="element-267">KHON2</a> · <a href="https://www.staradvertiser.com" className="hover:text-slate-400" target="_blank" rel="noopener noreferrer" data-id="element-268">Star-Advertiser</a> · <a href="https://www.hawaiianelectric.com" className="hover:text-slate-400" target="_blank" rel="noopener noreferrer" data-id="element-269">Hawaiian Electric</a> · All times HST (UTC−10)
          </p>
          <a href="https://www.teklani.com" target="_blank" rel="noopener noreferrer" className="neon-brand text-xs flex-shrink-0 flex items-center gap-1 hover:opacity-80 transition-opacity" data-id="element-270">
            Built by <HeartIcon className="w-3 h-3 fill-current inline" data-id="element-271" /> Teklani, LLC — Maui, HI
          </a>
        </div>
      </footer>
    </div>;
}